import React from "react";

import { useQuery } from "@tanstack/react-query";
import { FileCode, FileSpreadsheet } from "lucide-react";
import { useTranslation } from "react-i18next";

import BulkAPI from "../../api/BulkAPI";
import { downloadFile } from "../../utils/file";
import Button from "../button/Button";

import type { LucideIcon } from "lucide-react";
import type { JSX } from "react";

const EXPORT_FILENAME = "bulk_ingest";

interface BulkExportActionsProps {
  bulkId: string;
}

interface BulkExportButtonProps {
  icon: LucideIcon;
  isAvailable: boolean;
  label: string;
  onClick: () => void;
}

function BulkExportButton({
  icon: Icon,
  isAvailable,
  label,
  onClick,
}: BulkExportButtonProps): JSX.Element {
  const { t } = useTranslation("editor-bulk");

  return (
    <Button
      withGlow
      isDisabled={!isAvailable}
      onClick={onClick}
      size="xs"
      type="button"
    >
      <Icon className="size-3.75" />
      {isAvailable ? label : t("exportUnavailable")}
    </Button>
  );
}

function BulkExportActions({ bulkId }: BulkExportActionsProps): JSX.Element {
  const { t } = useTranslation("editor-bulk");

  const reportQuery = BulkAPI.generateQuery({
    fn: BulkAPI.getBulkReport,
    params: [{ bulkId }],
  });

  const jsonQuery = BulkAPI.generateQuery({
    fn: BulkAPI.getBulkJson,
    params: [{ bulkId }],
  });

  const { data: report } = useQuery(reportQuery);
  const { data: json } = useQuery(jsonQuery);

  return (
    <div className="flex flex-wrap gap-2">
      <BulkExportButton
        icon={FileSpreadsheet}
        isAvailable={report !== undefined}
        label={t("download_report")}
        onClick={(): void =>
          downloadFile({
            payload: new Blob([report ?? ""], { type: "text/csv" }),
            filename: EXPORT_FILENAME,
            fileExtension: "csv",
          })
        }
      />

      <BulkExportButton
        icon={FileCode}
        isAvailable={json !== undefined}
        label={t("download_json")}
        onClick={(): void =>
          downloadFile({
            payload: new Blob([JSON.stringify(json, null, "\t")], {
              type: "application/json",
            }),
            filename: EXPORT_FILENAME,
            fileExtension: "json",
          })
        }
      />
    </div>
  );
}

export default BulkExportActions;
